/**
 * SOAR Cart Drawer
 * Server-rendered side cart, refreshed via Shopify's Section Rendering API so the
 * markup / messaging / pricing match the live theme exactly (incl. "Before Savings"
 * compare-at totals and line-item properties, which /cart.js cannot provide).
 *
 * - Opens on cart-link click or `cart:add` event
 * - Quantity / remove via /cart/change.js (returns the re-rendered section)
 * - Dispatches `cart:updated` for the header badge
 */
(function () {
  const SECTION_ID = 'soar-cart-drawer';

  /** @param {string} html @param {(node: Element|null) => void} fn */
  function withRoot(html, fn) {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    fn(tmpl.content.querySelector('[data-cart-root]'));
  }

  class SoarCartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
      });

      // Open + refresh when something is added to the cart anywhere on the site
      document.addEventListener('cart:add', () => {
        this.refresh().then(() => this.open());
      });
      document.addEventListener('cart:refresh', () => this.refresh());

      // Header cart link opens the drawer instead of navigating to /cart
      document.addEventListener('click', (e) => {
        const trigger = /** @type {HTMLElement} */ (e.target).closest('[data-soar-cart-link]');
        if (trigger) {
          e.preventDefault();
          this.refresh().then(() => this.open());
        }
      });
    }

    /** @param {MouseEvent} e */
    onClick(e) {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.closest('[data-soar-cart-overlay]') || target.closest('[data-soar-cart-close]')) {
        this.close();
        return;
      }

      const qtyBtn = target.closest('[data-qty-action]');
      if (qtyBtn instanceof HTMLElement) {
        const key = qtyBtn.dataset.lineKey;
        const row = qtyBtn.closest('[data-line-key]');
        const valueEl = row && row.querySelector('.soar-cart-drawer__qty-value');
        const current = valueEl ? parseInt(valueEl.textContent || '0', 10) : 0;
        const delta = qtyBtn.dataset.qtyAction === 'increase' ? 1 : -1;
        if (key) this.changeLine(key, current + delta);
        return;
      }

      const removeBtn = target.closest('[data-remove]');
      if (removeBtn instanceof HTMLElement && removeBtn.dataset.lineKey) {
        this.changeLine(removeBtn.dataset.lineKey, 0);
      }
    }

    open() {
      this.dataset.open = 'true';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('soar-cart-open');
      /* Tawk paints its launcher above everything, so ask it to step aside. */
      try { if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') window.Tawk_API.hideWidget(); } catch (e) {}
    }

    close() {
      this.dataset.open = 'false';
      document.body.style.overflow = '';
      document.body.classList.remove('soar-cart-open');
      try { if (window.Tawk_API && typeof window.Tawk_API.showWidget === 'function') window.Tawk_API.showWidget(); } catch (e) {}
    }

    /** @param {string} html */
    applyHtml(html) {
      if (!html) return;
      withRoot(html, (fresh) => {
        const current = this.querySelector('[data-cart-root]');
        if (fresh && current) {
          current.replaceWith(fresh);
          const count = parseInt(/** @type {HTMLElement} */ (fresh).dataset.itemCount || '0', 10);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: count } }));
        }
      });
    }

    async refresh() {
      this.dataset.loading = 'true';
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        this.applyHtml(data[SECTION_ID]);
      } catch (err) {
        console.error('SOAR cart refresh failed', err);
      } finally {
        this.dataset.loading = 'false';
      }
    }

    /** @param {string} id @param {number} quantity */
    async changeLine(id, quantity) {
      this.dataset.loading = 'true';
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id,
            quantity: Math.max(0, quantity),
            sections: SECTION_ID,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (data.sections && data.sections[SECTION_ID]) {
          this.applyHtml(data.sections[SECTION_ID]);
        } else {
          await this.refresh();
        }
      } catch (err) {
        console.error('SOAR cart change failed', err);
      } finally {
        this.dataset.loading = 'false';
      }
    }
  }

  if (!customElements.get('soar-cart-drawer')) {
    customElements.define('soar-cart-drawer', SoarCartDrawer);
  }
})();
